/**
 * @file useVolPay.js
 * @description 全局购卷弹窗：preview / 批量档 / buy，余额不足转充值
 * @module features/comic/composables/useVolPay
 */

import { computed, ref } from 'vue'
import { getAuthToken } from '@/shared/utils/auth'
import { getApiErrorMessageByCode, ERR_GOLD_NOT_ENOUGH, ERR_NOT_LOGIN, ERR_VOL_PAY_EMPTY } from '@/shared/utils/apiErrorCodes'
import {
  registerVolPayOpener,
  registerVolPayRechargePreview,
  registerVolPayRetry,
  requestOpenGoldRecharge,
} from '@/shared/utils/payDialogBridge'
import { AUTH_CHANGED_EVENT, emitVolsUnlocked } from '@/shared/utils/authEvents'
import { useUserStore } from '@/stores/user'
import { buyVolPayApi, fetchVolPayPreviewApi, fetchVolPayRechargePreviewApi } from '../api/volPayApi'

const visible = ref(false)
const loading = ref(false)
const errorMessage = ref('')
const preview = ref(/** @type {import('../api/volPayTypes').VolPayPreviewVo|null} */ (null))
const selectedBulkType = ref(/** @type {import('../api/volPayTypes').VolPayBulkType} */ ('NONE'))
/** @type {import('vue').Ref<'home'|'bulk'|'success'>} */
const step = ref('home')

/** @type {{ comicId: string, volId: string, volNum?: number, onUnlocked?: Function }|null} */
let openContext = null
/** @type {Function|null} */
let pendingUnlock = null
/** @type {import('../api/volPayTypes').VolPayBuyVo|null} */
let pendingUnlockResult = null

/**
 * @param {unknown} err
 * @returns {string}
 */
function resolveVolPayError(err) {
  const code = err && typeof err === 'object' && 'code' in err ? err.code : null
  const byCode = getApiErrorMessageByCode(code)
  if (byCode) return byCode
  if (err instanceof Error && err.message) return err.message
  return '操作失败，请稍后再试'
}

function resetDialog() {
  loading.value = false
  errorMessage.value = ''
  preview.value = null
  selectedBulkType.value = 'NONE'
  step.value = 'home'
  pendingUnlock = null
  pendingUnlockResult = null
  openContext = null
}

/**
 * @param {import('../api/volPayTypes').VolPayBulkType} [bulkType]
 */
async function loadPreview(bulkType = 'NONE') {
  if (!openContext) return
  loading.value = true
  errorMessage.value = ''
  try {
    const data = await fetchVolPayPreviewApi({
      comicId: openContext.comicId,
      volId: openContext.volId,
      bulkType,
    })
    preview.value = data
    selectedBulkType.value = data.bulkType || bulkType
  } catch (err) {
    const code = err && typeof err === 'object' && 'code' in err ? err.code : null
    if (Number(code) === ERR_NOT_LOGIN) {
      errorMessage.value = resolveVolPayError(err)
      return
    }
    errorMessage.value = resolveVolPayError(err)
  } finally {
    loading.value = false
  }
}

/**
 * 打开购卷弹窗（须已登录）。
 * @param {{ comicId: string, volId: string, volNum?: number, onUnlocked?: Function }} context
 * @returns {boolean}
 */
export function openVolPay(context) {
  if (!getAuthToken()) return false
  if (!context?.comicId || !context?.volId) return false
  openContext = {
    comicId: String(context.comicId),
    volId: String(context.volId),
    volNum: context.volNum,
    onUnlocked: context.onUnlocked,
  }
  selectedBulkType.value = 'NONE'
  step.value = 'home'
  errorMessage.value = ''
  visible.value = true
  void loadPreview('NONE')
  return true
}

/**
 * 关闭购卷弹窗（购买成功、用户取消、登出、离开路由等）。
 */
export function closeVolPay() {
  visible.value = false
  resetDialog()
}

/**
 * 充值成功后重新 preview，余额够则自动 buy
 * @returns {Promise<boolean>}
 */
export async function retryVolPayAfterRecharge() {
  if (!openContext) return false
  visible.value = true
  await loadPreview(selectedBulkType.value || 'NONE')
  const action = preview.value?.actionType
  if (action === 'BUY') {
    return submitVolPay()
  }
  return false
}

registerVolPayOpener(openVolPay)
registerVolPayRetry(retryVolPayAfterRecharge)
registerVolPayRechargePreview(fetchVolPayRechargePreviewApi)

if (typeof window !== 'undefined') {
  window.addEventListener(AUTH_CHANGED_EVENT, () => {
    if (!getAuthToken() && visible.value) closeVolPay()
  })
}

/**
 * @param {import('../api/volPayTypes').VolPayBulkType} bulkType
 */
async function selectBulkType(bulkType) {
  if (loading.value || bulkType === selectedBulkType.value) return
  selectedBulkType.value = bulkType
  await loadPreview(bulkType)
}

/**
 * 进入批量购买屏，选中第一个可用档并重新 preview
 */
function goToBulkStep() {
  const first = (preview.value?.bulkOptions || []).find(
    (opt) => opt.available !== false && opt.bulkType !== 'NONE'
  )
  if (!first) return
  step.value = 'bulk'
  void selectBulkType(first.bulkType)
}

/**
 * 返回购卷首页，恢复本卷档 preview
 */
function goToHomeStep() {
  step.value = 'home'
  void selectBulkType('NONE')
}

/**
 * 购买成功提示确认后关窗，再刷新页图进入阅读
 */
function confirmVolPaySuccess() {
  const onUnlocked = pendingUnlock
  const result = pendingUnlockResult
  pendingUnlock = null
  pendingUnlockResult = null
  closeVolPay()
  if (typeof onUnlocked === 'function') onUnlocked(result)
}

async function submitVolPay() {
  if (!openContext || !preview.value || loading.value) return false
  const action = preview.value.actionType
  if (action === 'RECHARGE' || action === 'FIRST_RECHARGE') {
    requestOpenGoldRecharge({
      source: 'vol-pay',
      comicId: openContext.comicId,
      volId: openContext.volId,
      bulkType: selectedBulkType.value || 'NONE',
      fetchPreview: () =>
        fetchVolPayRechargePreviewApi({
          comicId: openContext.comicId,
          volId: openContext.volId,
          bulkType: selectedBulkType.value || 'NONE',
        }),
      onPaid: () => retryVolPayAfterRecharge(),
    })
    return true
  }

  loading.value = true
  errorMessage.value = ''
  try {
    const result = await buyVolPayApi({
      comicId: openContext.comicId,
      volId: openContext.volId,
      bulkType: selectedBulkType.value || 'NONE',
    })
    const userStore = useUserStore()
    void userStore.fetchUserInfo()
    emitVolsUnlocked({
      comicId: openContext.comicId,
      unlockedVolIds: result?.unlockedVolIds || [],
    })
    pendingUnlock = openContext.onUnlocked
    pendingUnlockResult = result
    step.value = 'success'
    return true
  } catch (err) {
    const code = err && typeof err === 'object' && 'code' in err ? Number(err.code) : null
    if (code === ERR_GOLD_NOT_ENOUGH) {
      requestOpenGoldRecharge({
        source: 'vol-pay',
        comicId: openContext.comicId,
        volId: openContext.volId,
        bulkType: selectedBulkType.value || 'NONE',
        fetchPreview: () =>
          fetchVolPayRechargePreviewApi({
            comicId: openContext.comicId,
            volId: openContext.volId,
            bulkType: selectedBulkType.value || 'NONE',
          }),
        onPaid: () => retryVolPayAfterRecharge(),
      })
      return false
    }
    if (code === ERR_VOL_PAY_EMPTY) {
      errorMessage.value = resolveVolPayError(err)
      return false
    }
    errorMessage.value = resolveVolPayError(err)
    return false
  } finally {
    loading.value = false
  }
}

export function useVolPay() {
  const availableBulkOptions = computed(() =>
    (preview.value?.bulkOptions || []).filter(
      (opt) => opt.available !== false && opt.bulkType !== 'NONE'
    )
  )
  const actionText = computed(() => preview.value?.actionText || '立即购买')
  const showBulkEntry = computed(() => availableBulkOptions.value.length > 0)
  const agreementTitle = computed(
    () => preview.value?.agreementTitle || '橘子漫嘎付费内容购买协议'
  )
  const agreementCode = computed(() => preview.value?.agreementCode || 'VOL_PAY')

  return {
    visible,
    loading,
    errorMessage,
    preview,
    selectedBulkType,
    step,
    availableBulkOptions,
    actionText,
    showBulkEntry,
    agreementTitle,
    agreementCode,
    openVolPay,
    closeVolPay,
    selectBulkType,
    goToBulkStep,
    goToHomeStep,
    confirmVolPaySuccess,
    submitVolPay,
  }
}
